import { G } from '@mobily/ts-belt';
import cookie from 'browser-cookies';
import dayjs from 'dayjs';
import { z } from 'zod';
import type { FlowContext } from './flow-machine';
import { flows } from './flows';
import type { FixAutoShipInterval, FixScheduleInput } from '../../../../types/definitions/vendor/graphql-schema.d';
export const CADENCE_COOKIE_NAME = 'cob_lff_cadence';
const intervalSchema: z.ZodType<FixAutoShipInterval> = z.union([z.literal('EVERY_MONTH'), z.literal('EVERY_OTHER_MONTH'), z.literal('EVERY_THREE_MONTHS'), z.literal('EVERY_TWO_THREE_WEEKS'), z.literal('NONE')]);
const cadenceSchema = z.object({
  interval: intervalSchema.optional(),
  clientWantsFixOn: z.string().optional(),
  firstAvailableFixDate: z.string().optional()
});
export const persistStepData = (data: Partial<FixScheduleInput> = {}) => {
  cookie.set(CADENCE_COOKIE_NAME, JSON.stringify({
    interval: data?.interval,
    clientWantsFixOn: data?.clientWantsFixOn,
    firstAvailableFixDate: data?.firstAvailableFixDate
  }), {
    expires: dayjs().add(1, 'hour').toDate()
  });
};
export const clearPersistedStepData = () => {
  cookie.erase(CADENCE_COOKIE_NAME);
};
export const getPersistedStepData = (requestCookies: Partial<Record<string, string>> = {}) => {
  try {
    return cadenceSchema.parse(JSON.parse(decodeURIComponent(requestCookies[CADENCE_COOKIE_NAME] ?? '{}')));
  } catch (e) {
    return {};
  }
};
export const getFlowSteps = (context: FlowContext) => flows[context.flow].filter(step => {
  const businessLineVisible = !context.businessLine || step.restrictedBusinessLines === undefined || step.restrictedBusinessLines?.includes(context.businessLine) === true;
  const stepVisible = step.isVisible?.(context) ?? true;
  return businessLineVisible && stepVisible;
});
export const getStepIndex = (context: FlowContext, step?: string) => getFlowSteps(context).findIndex(f => f.step === step);
export const getFirstAvailableStepIndex = (context: FlowContext) => getFlowSteps(context).length > 0 ? 0 : -1;

/**
 * Builds the router path from a flow and identifier
 * @param flow flowId
 * @param identifier of the step, as an index of the flow or a stepId name
 * @param fallback the path returned if identifier does not exist in flow
 * @returns the path for the flow and step identifier
 */
export const getStepPath = ({
  identifier,
  fallback = '/',
  ...context
}: FlowContext & {
  identifier: number | string;
  fallback?: string;
}) => {
  const flowSteps = getFlowSteps(context);
  const step = G.isString(identifier) ? flowSteps.find(f => f.step === identifier) : flowSteps[identifier];
  if (G.isUndefined(step)) {
    return fallback;
  }
  return `/onboarding/get-your-fix/${context.flow}/${step.step}`;
};
export const getSteps = ({
  currentStepIndex,
  ...context
}: FlowContext) => ({
  currentStepIndex,
  nextStepPath: getStepPath({
    ...context,
    currentStepIndex,
    identifier: currentStepIndex + 1
  }),
  previousStepPath: getStepPath({
    ...context,
    currentStepIndex,
    identifier: currentStepIndex - 1,
    fallback: '/onboarding/profile-note'
  })
});